import type { FC, ReactNode } from 'react'
import type { ActivityType, Customer, Invoice } from '@/types'

import { useEffect, useMemo, useState } from 'react'
import { Mail, MapPin, Receipt, UserRound } from 'lucide-react'
import { createRandom, hashString } from '@/lib/random'
import { formatRupiah } from '@/lib/format'
import { TODAY, addDays, daysBetween, formatDate, formatRelative } from '@/lib/date'
import { Button, buttonVariants } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { EntityAvatar } from '@/components/dashboard/entity-avatar'
import { PlanBadge } from '@/components/dashboard/plan-badge'
import { CustomerStatusBadge, InvoiceStatusBadge } from '@/components/dashboard/status-badge'
import { ActivityIcon } from '@/components/dashboard/activity-icon'
import { EmptyState } from '@/components/dashboard/empty-state'
import { HealthMeter } from '@/components/customers/health-meter'
import { getCustomerInvoices, nextBillingDate } from '@/data/invoices'
import { getPlan } from '@/data/plans'
import { CYCLE_LABELS, PAYMENT_METHOD_LABELS } from '@/constants/dashboard'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

interface CustomerSheetProps {
    customer: Customer | null
    onOpenChange: (open: boolean) => void
    onChangePlan: (customer: Customer) => void
    onCancel: (customer: Customer) => void
    onReactivate: (customer: Customer) => void
}

type TimelineEvent = {
    id: string
    date: string
    type: ActivityType
    title: string
    description: string
}

const buildTimeline = (customer: Customer, invoices: Invoice[]): TimelineEvent[] => {
    const random = createRandom(hashString(`timeline-${customer.id}`))
    const plan = getPlan(customer.plan)
    const events: TimelineEvent[] = [
        {
            id: 'joined',
            date: customer.joinedAt,
            type: customer.status === 'trial' ? 'trial' : 'new',
            title: customer.status === 'trial' ? `Memulai trial paket ${plan.name}` : `Berlangganan paket ${plan.name}`,
            description: `Didaftarkan oleh ${customer.contact}`
        }
    ]

    if (customer.status !== 'canceled') {
        events.push({
            id: 'login',
            date: addDays(TODAY, -random.int(0, Math.min(4, daysBetween(customer.joinedAt, TODAY)))),
            type: 'team',
            title: `Login terakhir oleh ${customer.contact}`,
            description: 'Melalui aplikasi web Arunika'
        })
    }

    invoices.slice(0, 3).forEach((invoice) => {
        events.push({
            id: invoice.id,
            date: invoice.paidAt ?? invoice.issuedAt,
            type: invoice.status === 'failed' ? 'payment-failed' : 'payment',
            title: invoice.status === 'failed'
                ? `Pembayaran ${invoice.number} gagal`
                : invoice.status === 'pending'
                    ? `Tagihan ${invoice.number} diterbitkan`
                    : `Pembayaran ${invoice.number} diterima`,
            description: `${formatRupiah(invoice.amount)} melalui ${PAYMENT_METHOD_LABELS[invoice.method]}`
        })
    })

    const extraSeats = customer.seats - plan.includedSeats
    const age = daysBetween(customer.joinedAt, TODAY)

    if (extraSeats > 0 && age > 20 && customer.status !== 'trial') {
        events.push({
            id: 'seats',
            date: addDays(customer.joinedAt, random.int(10, age - 5)),
            type: 'upgrade',
            title: `Menambah ${extraSeats} pengguna`,
            description: `Total ${customer.seats} pengguna aktif`
        })
    }

    if (customer.status === 'at-risk') {
        events.push({
            id: 'risk',
            date: addDays(TODAY, -random.int(1, 6)),
            type: 'risk',
            title: `Skor kesehatan turun ke ${customer.health}`,
            description: 'Penggunaan produk menurun dalam 14 hari terakhir'
        })
    }

    if (customer.status === 'canceled' && customer.canceledAt) {
        events.push({
            id: 'canceled',
            date: customer.canceledAt,
            type: 'churn',
            title: 'Membatalkan langganan',
            description: 'Akses berakhir di akhir periode tagihan'
        })
    }

    return events.sort((a, b) => (a.date === b.date ? 0 : a.date < b.date ? 1 : -1))
}

interface StatProps {
    label: string
    children: ReactNode
}

const Stat: FC<StatProps> = ({ label, children }): ReactNode => (
    <div className='rounded-lg border bg-card p-3'>
        <dt className='text-xs text-muted-foreground'>
            {label}
        </dt>
        <dd className='mt-1 text-sm font-semibold text-foreground'>
            {children}
        </dd>
    </div>
)

interface DetailRowProps {
    label: string
    children: ReactNode
}

const DetailRow: FC<DetailRowProps> = ({ label, children }): ReactNode => (
    <div className='flex items-center justify-between gap-4 py-2.5 text-sm'>
        <dt className='text-muted-foreground'>
            {label}
        </dt>
        <dd className='text-right font-medium text-foreground'>
            {children}
        </dd>
    </div>
)

export const CustomerSheet: FC<CustomerSheetProps> = ({ customer, onOpenChange, onChangePlan, onCancel, onReactivate }): ReactNode => {
    // Keep the last customer on screen while the panel slides out
    const [displayed, setDisplayed] = useState<Customer | null>(customer)

    useEffect(() => {
        if (customer) setDisplayed(customer)
    }, [customer])

    const current = customer ?? displayed
    const invoices = useMemo(() => (current ? getCustomerInvoices(current) : []), [current])
    const timeline = useMemo(() => (current ? buildTimeline(current, invoices) : []), [current, invoices])

    return (
        <Sheet open={customer !== null} onOpenChange={onOpenChange}>
            <SheetContent side='right' className='gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-lg'>
                {current && (
                    <>
                        <SheetHeader className='gap-4 border-b p-5 pr-12'>
                            <div className='flex items-start gap-3'>
                                <EntityAvatar name={current.company} size='lg' />

                                <div className='min-w-0'>
                                    <SheetTitle className='text-lg leading-snug font-semibold'>
                                        {current.company}
                                    </SheetTitle>
                                    <SheetDescription className='font-mono text-xs'>
                                        {current.id}
                                    </SheetDescription>
                                    <div className='mt-2 flex flex-wrap gap-1.5'>
                                        <CustomerStatusBadge status={current.status} />
                                        <PlanBadge plan={current.plan} />
                                    </div>
                                </div>
                            </div>

                            <ul className='space-y-1.5 text-sm text-muted-foreground'>
                                <li className='flex items-center gap-2'>
                                    <UserRound className='size-4 shrink-0' aria-hidden='true' />
                                    <span className='text-foreground'>{current.contact}</span>
                                </li>
                                <li className='flex items-center gap-2'>
                                    <Mail className='size-4 shrink-0' aria-hidden='true' />
                                    <a href={`mailto:${current.email}`} className='truncate text-foreground underline-offset-4 hover:underline'>
                                        {current.email}
                                    </a>
                                </li>
                                <li className='flex items-center gap-2'>
                                    <MapPin className='size-4 shrink-0' aria-hidden='true' />
                                    <span>{current.city}, {current.province}</span>
                                </li>
                            </ul>
                        </SheetHeader>

                        <div className='flex-1 overflow-y-auto p-5'>
                            <dl className='grid grid-cols-2 gap-3'>
                                <Stat label={current.status === 'trial' ? 'MRR setelah trial' : current.status === 'canceled' ? 'MRR sebelumnya' : 'MRR'}>
                                    <span className='tabular-nums'>{formatRupiah(current.mrr)}</span>
                                </Stat>
                                <Stat label='Pengguna'>
                                    <span className='tabular-nums'>{current.seats}</span>
                                    <span className='font-normal text-muted-foreground'> dari {Math.max(current.seats, getPlan(current.plan).includedSeats)} kursi</span>
                                </Stat>
                                <Stat label='Bergabung'>
                                    {formatDate(current.joinedAt)}
                                    <span className='block text-xs font-normal text-muted-foreground'>{formatRelative(current.joinedAt)}</span>
                                </Stat>
                                <Stat label='Skor kesehatan'>
                                    <HealthMeter score={current.health} showLabel className='-ml-1.5' />
                                </Stat>
                            </dl>

                            <Tabs key={current.id} defaultValue='subscription' className='mt-6'>
                                <TabsList className='w-full'>
                                    <TabsTrigger value='subscription'>Langganan</TabsTrigger>
                                    <TabsTrigger value='invoices'>Tagihan</TabsTrigger>
                                    <TabsTrigger value='activity'>Aktivitas</TabsTrigger>
                                </TabsList>

                                <TabsContent value='subscription' className='pt-2'>
                                    <dl className='divide-y'>
                                        <DetailRow label='Paket'>{getPlan(current.plan).name}</DetailRow>
                                        <DetailRow label='Siklus tagihan'>{CYCLE_LABELS[current.cycle]}</DetailRow>
                                        <DetailRow label='Harga paket'>
                                            {formatRupiah(current.cycle === 'annual' ? getPlan(current.plan).annualPrice : getPlan(current.plan).price)} / bulan
                                        </DetailRow>
                                        {current.status === 'trial' && current.trialEndsAt && (
                                            <DetailRow label='Trial berakhir'>{formatDate(current.trialEndsAt)}</DetailRow>
                                        )}
                                        {current.status === 'canceled' && current.canceledAt && (
                                            <DetailRow label='Dibatalkan'>{formatDate(current.canceledAt)}</DetailRow>
                                        )}
                                        {(current.status === 'active' || current.status === 'at-risk') && (
                                            <DetailRow label='Tagihan berikutnya'>{formatDate(nextBillingDate(current))}</DetailRow>
                                        )}
                                        <DetailRow label='Metode pembayaran'>{PAYMENT_METHOD_LABELS[current.paymentMethod]}</DetailRow>
                                    </dl>
                                </TabsContent>

                                <TabsContent value='invoices' className='pt-2'>
                                    {invoices.length === 0
                                        ? (
                                            <EmptyState
                                                icon={Receipt}
                                                title='Belum ada tagihan'
                                                description='Tagihan pertama diterbitkan setelah masa trial berakhir.'
                                                className='py-10'
                                            />
                                        )
                                        : (
                                            <ul className='divide-y'>
                                                {invoices.map((invoice) => (
                                                    <li key={invoice.id} className='flex items-center justify-between gap-3 py-3'>
                                                        <div className='min-w-0'>
                                                            <p className='font-mono text-xs text-foreground'>{invoice.number}</p>
                                                            <p className='mt-0.5 text-xs text-muted-foreground'>{formatDate(invoice.issuedAt)}</p>
                                                        </div>
                                                        <div className='flex items-center gap-3'>
                                                            <span className='text-sm font-medium text-foreground tabular-nums'>{formatRupiah(invoice.amount)}</span>
                                                            <InvoiceStatusBadge status={invoice.status} />
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                </TabsContent>

                                <TabsContent value='activity' className='pt-4'>
                                    <ol className='space-y-4'>
                                        {timeline.map((event) => (
                                            <li key={event.id} className='flex gap-3'>
                                                <ActivityIcon type={event.type} />
                                                <div className='min-w-0'>
                                                    <p className='text-sm font-medium text-foreground'>{event.title}</p>
                                                    <p className='text-xs text-muted-foreground'>{event.description}</p>
                                                    <p className='mt-0.5 text-[11px] text-muted-foreground/80'>
                                                        {formatDate(event.date)}, {formatRelative(event.date).toLowerCase()}
                                                    </p>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                </TabsContent>
                            </Tabs>
                        </div>

                        <div className='flex flex-wrap items-center gap-2 border-t p-4'>
                            <a href={`mailto:${current.email}`} className={buttonVariants({ variant: 'outline' })}>
                                <Mail data-icon='inline-start' />
                                Kirim email
                            </a>

                            {current.status !== 'canceled' && (
                                <Button variant='outline' onClick={() => onChangePlan(current)}>
                                    Ubah paket
                                </Button>
                            )}

                            <div className='ml-auto'>
                                {current.status === 'canceled'
                                    ? (
                                        <Button onClick={() => onReactivate(current)}>
                                            Aktifkan kembali
                                        </Button>
                                    )
                                    : (
                                        <Button variant='destructive' onClick={() => onCancel(current)}>
                                            Batalkan langganan
                                        </Button>
                                    )}
                            </div>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    )
}
